import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import ModuloHeader from '../ModuloHeader';
import {
    ArrowLeft, Check, Circle, Clock, GitCommit, MessageSquare, Send, Ban,
    Trash2, Undo2, Plus, Pencil, X, RotateCcw,
} from 'lucide-react';
import { COR, COR_ESCURA, COR_FUNDO, BORDA, BORDA_SUAVE, TEXTO, doStatus, PRIORIDADE, STATUS, TIPOS, lerLocal, gravarLocal } from './demandasUI';

// =====================================================================
// Demandas — a PÁGINA de uma demanda (rota `/demandas/:codigo`)
//
// ⚠️ Era um painel deslizante sobre o quadro até 17/09, e virou página por
//    pedido do Rodrigo. O ganho não é só de espaço: com rota própria a demanda
//    tem **URL**, e URL se guarda, se manda para a Julia e se abre em outra aba
//    com o clique do meio. Painel não tem endereço.
//
// O que a GESTÃO (Admin) faz aqui: acompanhar, triar, concluir tarefa, **criar
// tarefa**, escrever andamento e excluir. A criação em lote e o registro do dia
// a dia continuam no `server/demandas/cli.js` — mas a tela deixou de ser só leitura.
//
// ⚠️ A `Diretoria` (28/09) VÊ tudo, mas só mexe no PRÓPRIO pedido e só enquanto
//    ele está "Pendente de triagem" (corrigir/excluir). Depois de triado, é
//    leitura para ela. O recorte é `podeGerir`/`podeMexerPedido` abaixo, e espelha
//    o `decidirEscritaDemanda` do backend.
// =====================================================================

const API = '/api/demandas';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const authJson = () => ({ ...auth(), 'Content-Type': 'application/json' });

const quando = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ` +
           `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Data + hora com o ano, para os commits (que atravessam vários dias/anos do
// histórico): "AAAA-MM-DD HH:MM". Mesma leitura de hora local que o `quando`.
const dataHora = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Data + hora no formato BR ("dd/mm/aaaa hh:mm"), para a nota discreta de datas
// embaixo de cada tarefa (criação sempre; conclusão quando houver).
const dataHoraBR = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Usuário logado (localStorage 'user' = { id, nome, perfil, loja }). `podeGerir`
// espelha o PERFIS_GESTAO do backend (só Admin tria/edita/exclui livremente); a
// diretoria só mexe no PRÓPRIO pedido enquanto ele está "Pendente de triagem".
function lerUsuario() {
    try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        return { perfil: u.perfil || null, id: u.id ?? null };
    } catch { return { perfil: null, id: null }; }
}

function DemandaDetalhe() {
    const { codigo } = useParams();
    const navigate = useNavigate();

    const { perfil: meuPerfil, id: meuId } = lerUsuario();
    const podeGerir = meuPerfil === 'Admin';
    // Painel de correção do próprio pedido (só solicitante, só pré-triagem).
    const [editandoPedido, setEditandoPedido] = useState(false);
    const [pedido, setPedido] = useState({ titulo: '', descricao: '', prioridade: 'Media', tipo: '', modulo: '' });

    const [isMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);
    // Rascunhos persistidos POR DEMANDA (a chave leva o `codigo`): o que a
    // pessoa começou a escrever aqui continua se ela sair e voltar. Somem ao
    // enviar (o reset do estado grava o valor vazio). Ver lerLocal em demandasUI.
    const [novoAndamento, setNovoAndamento] = useState(() => lerLocal(`andamento:${codigo}`, ''));
    const [verExcluidas, setVerExcluidas] = useState(false);

    // Formulário de tarefa nova. A seção fica "grudada" depois de criar uma —
    // quem está destrinchando uma demanda acrescenta vários itens seguidos na
    // MESMA seção, e reescrever o nome a cada item é atrito à toa.
    const [novaTarefa, setNovaTarefa] = useState(() => lerLocal(`novaTarefa:${codigo}`, { desc: '', secao: '' }));
    const [equipe, setEquipe] = useState([]);
    const [modulos, setModulos] = useState([]);

    // A lista de possíveis responsáveis. Carregada UMA vez: ela não muda durante
    // a visita, e recarregá-la a cada gravação seria pedido inútil ao servidor.
    // ⚠️ Falha aqui é silenciosa de propósito — sem a lista o seletor fica vazio,
    //    mas o resto da página continua inteiro; não vale derrubar a demanda toda
    //    porque um seletor não encheu.
    useEffect(() => {
        fetch(`${API}/equipe`, { headers: auth() })
            .then((r) => (r.ok ? r.json() : []))
            .then(setEquipe)
            .catch(() => setEquipe([]));
        fetch(`${API}/modulos`, { headers: auth() })
            .then((r) => (r.ok ? r.json() : []))
            .then(setModulos)
            .catch(() => setModulos([]));
    }, []);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro('');
        try {
            const r = await fetch(`${API}/${codigo}${verExcluidas ? '?excluidas=1' : ''}`, { headers: auth() });
            if (r.status === 404) throw new Error('Demanda não encontrada.');
            if (!r.ok) throw new Error(`A demanda não pôde ser carregada (${r.status}).`);
            setDados(await r.json());
        } catch (e) {
            setErro(e.message);
        } finally {
            setCarregando(false);
        }
    }, [codigo, verExcluidas]);

    useEffect(() => { carregar(); }, [carregar]);

    // Persiste os rascunhos (por demanda) a cada tecla — leve, é texto curto.
    // Ao enviar, o estado é resetado e este efeito grava o vazio, limpando-o.
    useEffect(() => { gravarLocal(`andamento:${codigo}`, novoAndamento); }, [codigo, novoAndamento]);
    useEffect(() => { gravarLocal(`novaTarefa:${codigo}`, novaTarefa); }, [codigo, novaTarefa]);

    const escrever = async (url, metodo, corpo) => {
        setSalvando(true);
        setErro('');
        try {
            const r = await fetch(url, {
                method: metodo,
                headers: authJson(),
                body: corpo ? JSON.stringify(corpo) : undefined,
            });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j.message || `Não foi possível salvar (${r.status}).`);
            }
            await carregar();
            return true;
        } catch (e) {
            setErro(e.message);
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const alternarTarefa = (t) =>
        escrever(`${API}/tarefa/${t.ID}`, 'PATCH', { status: t.STATUS === 'Concluida' ? 'A fazer' : 'Concluida' });

    // Edição inline do texto de uma tarefa (o mesmo que o CLI faz por
    // `editar-tarefa`). `tarefaEditando` guarda o ID da tarefa em edição;
    // `textoEditando`, o rascunho. Só é oferecida com a DEMANDA aberta.
    const [tarefaEditando, setTarefaEditando] = useState(null);
    const [textoEditando, setTextoEditando] = useState('');
    const iniciarEdicao = (t) => { setTarefaEditando(t.ID); setTextoEditando(t.DESCRICAO); };
    const cancelarEdicao = () => { setTarefaEditando(null); setTextoEditando(''); };
    const salvarEdicao = async (t) => {
        const texto = textoEditando.trim();
        // Vazio ou inalterado: nada a gravar, só sai do modo de edição.
        if (!texto || texto === t.DESCRICAO) { cancelarEdicao(); return; }
        const ok = await escrever(`${API}/tarefa/${t.ID}`, 'PATCH', { descricao: texto });
        if (ok) cancelarEdicao();
    };

    const mudarStatus = (novo) => escrever(`${API}/${codigo}`, 'PATCH', { status: novo });

    /** Salva um atributo do cabeçalho (prioridade, tipo, responsável, módulo…). */
    const salvarCampo = (campos) => escrever(`${API}/${codigo}`, 'PATCH', campos);

    // Cadastra um módulo novo em dtbModulos e já o seleciona nesta demanda. Fica
    // aqui, na tela de detalhe, para não sobrecarregar a criação rápida — módulo
    // novo é evento raro e quem tria é quem sabe o nome certo.
    const criarModulo = async () => {
        const nome = window.prompt('Nome do novo módulo (aparecerá na lista para todas as demandas):');
        if (nome === null) return;
        const limpo = nome.trim();
        if (!limpo) return;
        setSalvando(true);
        setErro('');
        try {
            const r = await fetch(`${API}/modulos`, { method: 'POST', headers: authJson(), body: JSON.stringify({ nome: limpo }) });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j.message || `Não foi possível criar o módulo (${r.status}).`);
            }
            const criado = await r.json();
            const lista = await fetch(`${API}/modulos`, { headers: auth() }).then((x) => x.json()).catch(() => modulos);
            setModulos(lista);
            await salvarCampo({ modulo: criado.nome || limpo });
        } catch (e) {
            setErro(e.message);
        } finally {
            setSalvando(false);
        }
    };

    const criarTarefa = async () => {
        const desc = novaTarefa.desc.trim();
        if (!desc) return;
        const ok = await escrever(`${API}/${codigo}/tarefa`, 'POST', {
            descricao: desc,
            secao: novaTarefa.secao.trim() || null,
        });
        // Só limpa a descrição: a seção continua para o próximo item.
        if (ok) setNovaTarefa((v) => ({ ...v, desc: '' }));
    };

    // ⚠️ EXCLUIR ≠ RECUSAR, e é na tela que a confusão nasceria — por isso o
    //    texto do prompt fala em duplicada/criada errada, e lembra que "não
    //    vamos fazer" é o status Recusada, que continua visível com o motivo.
    const excluirDemanda = async () => {
        const motivo = window.prompt(
            'Excluir esta demanda.\n\n' +
            'Use para ERRO DE REGISTRO (duplicada, criada errada). Se a decisão foi "não vamos fazer", ' +
            'o certo é o status Recusada, que continua visível com o motivo.\n\nMotivo da exclusão:'
        );
        if (motivo === null) return;
        if (!motivo.trim()) return setErro('A exclusão exige um motivo.');
        const ok = await escrever(`${API}/${codigo}`, 'DELETE', { motivo: motivo.trim() });
        if (ok) navigate('/demandas');
    };

    const excluirTarefa = async (t) => {
        const motivo = window.prompt(`Excluir a tarefa "${t.DESCRICAO.slice(0, 60)}".\n\nMotivo:`);
        if (motivo === null) return;
        if (!motivo.trim()) return setErro('A exclusão exige um motivo.');
        await escrever(`${API}/tarefa/${t.ID}`, 'DELETE', { motivo: motivo.trim() });
    };

    // Muda o STATUS de uma tarefa e, junto, grava o porquê como andamento da
    // demanda — tudo com UMA recarga no fim (não duas). É o motor do Cancelar e
    // do Reativar. Por que o andamento? A tarefa NÃO tem coluna de "motivo do
    // cancelamento" (só a exclusão tem `EXCLUIDO_MOTIVO`); o andamento É esse
    // registro, e é o que mantém a decisão VISÍVEL — que é a diferença toda
    // entre cancelar (decisão) e excluir (erro de registro, some da lista).
    // ⚠️ O andamento é secundário: se o status já mudou e o andamento falhar,
    //    NÃO revertemos o status — a operação principal deu certo.
    const mudarStatusTarefa = async (t, status, nota) => {
        setSalvando(true);
        setErro('');
        try {
            const r = await fetch(`${API}/tarefa/${t.ID}`, {
                method: 'PATCH', headers: authJson(), body: JSON.stringify({ status }),
            });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j.message || `Não foi possível atualizar a tarefa (${r.status}).`);
            }
            if (nota) {
                await fetch(`${API}/${codigo}/andamento`, {
                    method: 'POST', headers: authJson(), body: JSON.stringify({ texto: nota }),
                }).catch(() => { /* não reverte o status já mudado */ });
            }
            await carregar();
        } catch (e) {
            setErro(e.message);
        } finally {
            setSalvando(false);
        }
    };

    // ⚠️ CANCELAR ≠ EXCLUIR. Cancelar é para a tarefa que FAZIA sentido e deixou
    //    de fazer: fica riscada e visível como memória da decisão. Excluir é para
    //    ERRO DE REGISTRO (duplicada, criada errada) e some da lista. O motivo é
    //    obrigatório justamente porque ele é a memória da decisão.
    const cancelarTarefa = async (t) => {
        const motivo = window.prompt(
            `Cancelar a tarefa "${t.DESCRICAO.slice(0, 60)}".\n\n` +
            'Use quando a tarefa fazia sentido e deixou de fazer — ela continua VISÍVEL (riscada), ' +
            'diferente de excluir, que é para erro de registro.\n\nMotivo do cancelamento:'
        );
        if (motivo === null) return;
        if (!motivo.trim()) return setErro('O cancelamento exige um motivo — ele é a memória da decisão.');
        const rotulo = t.NUM != null ? `Tarefa ${t.NUM}` : 'Tarefa';
        await mudarStatusTarefa(t, 'Cancelada', `${rotulo} cancelada: ${motivo.trim()}`);
    };

    // Desfaz o cancelamento (volta para "A fazer"). Existe pela mesma razão do
    // restaurar da exclusão: decisão sem volta é decisão com mais passos, e
    // "cancelei a errada" acontece com quem corre.
    const reativarTarefa = (t) => {
        const rotulo = t.NUM != null ? `Tarefa ${t.NUM}` : 'Tarefa';
        return mudarStatusTarefa(t, 'A fazer', `${rotulo} reativada (voltou para A fazer).`);
    };

    const enviarAndamento = async () => {
        if (!novoAndamento.trim()) return;
        const ok = await escrever(`${API}/${codigo}/andamento`, 'POST', { texto: novoAndamento.trim() });
        if (ok) setNovoAndamento('');
    };

    const est = doStatus(dados?.STATUS);
    // A demanda está "aberta" enquanto não foi encerrada (Concluída/Recusada).
    // É essa condição que libera a edição do texto das tarefas: numa demanda já
    // encerrada o checklist é registro histórico e não se reescreve.
    const demandaAberta = !!dados && dados.STATUS !== 'Concluida' && dados.STATUS !== 'Recusada';

    // O solicitante (diretoria) só mexe no PRÓPRIO pedido e só antes da triagem —
    // espelha exatamente o `decidirEscritaDemanda` do backend. Depois de triado,
    // vira leitura para ele.
    const preTriagem = !!dados && dados.STATUS === 'Pendente de triagem';
    const souDono = !!dados && meuId != null && dados.ID_SOLICITANTE === meuId;
    const podeMexerPedido = !podeGerir && souDono && preTriagem;

    // Abre o painel de correção já preenchido com o que a pessoa escreveu (o
    // texto ORIGINAL — é o que ela edita antes da triagem).
    const abrirEdicaoPedido = () => {
        setPedido({
            titulo: dados.TITULO_ORIGINAL || '',
            descricao: dados.DESCRICAO_ORIGINAL || '',
            prioridade: dados.PRIORIDADE || 'Media',
            tipo: dados.TIPO || '',
            modulo: dados.MODULO || '',
        });
        setEditandoPedido(true);
    };

    const salvarPedido = async () => {
        if (!pedido.titulo.trim()) return;
        const ok = await escrever(`${API}/${codigo}`, 'PATCH', {
            tituloOriginal: pedido.titulo.trim(),
            descricaoOriginal: pedido.descricao.trim() || null,
            prioridade: pedido.prioridade,
            tipo: pedido.tipo || null,
            modulo: pedido.modulo || null,
        });
        if (ok) setEditandoPedido(false);
    };

    // Exclusão pelo próprio solicitante: confirmação simples (o motivo-padrão é
    // preenchido pelo backend), pois é o pedido dele, criado por engano.
    const excluirMeuPedido = async () => {
        if (!window.confirm('Excluir este pedido? Você pode criar outro depois.')) return;
        const ok = await escrever(`${API}/${codigo}`, 'DELETE');
        if (ok) navigate('/demandas');
    };
    const margemEsquerda = window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px');

    const caixa = {
        background: '#fff', border: `1px solid ${BORDA}`, borderRadius: 14,
        padding: 24, marginBottom: 20, boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    };
    const campoEstilo = {
        padding: '8px 10px', borderRadius: 8, border: `1px solid ${BORDA}`,
        fontSize: 13.5, fontFamily: 'inherit', color: TEXTO, background: '#fff', width: '100%',
        boxSizing: 'border-box',
    };
    // Os quadros de histórico (Andamentos e Commits) cresciam sem limite: numa
    // demanda com muitos registros eles esticavam a página inteira. Altura-teto
    // + rolagem PRÓPRIA — o card fica previsível e o histórico rola DENTRO do
    // quadro. `overflowY: auto` só mostra a barra quando passa do teto, então
    // demanda curta continua natural (sem espaço vazio forçado). O paddingRight
    // afasta o texto da barra; o marginRight negativo devolve o espaço quando
    // não há rolagem, para o conteúdo não parecer "encolhido".
    const listaRolavel = { maxHeight: 340, overflowY: 'auto', paddingRight: 8, marginRight: -8 };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            <div style={{ flex: 1, marginLeft: margemEsquerda, transition: 'margin-left .3s ease' }}>

                <ModuloHeader
                    eyebrow="Demandas"
                    titulo={dados?.CODIGO || codigo}
                    cor={COR}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1100, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                    <button onClick={() => navigate('/demandas')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7, marginBottom: 18,
                            padding: '8px 14px', borderRadius: 8, border: `1px solid ${BORDA}`,
                            background: '#fff', color: '#475569', fontWeight: 600, fontSize: 13.5, cursor: 'pointer',
                        }}>
                        <ArrowLeft size={16} /> Voltar ao quadro
                    </button>

                    {erro && (
                        <div style={{
                            background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
                            padding: '11px 14px', borderRadius: 10, fontSize: 13.5, marginBottom: 18,
                        }}>{erro}</div>
                    )}

                    {carregando && !dados && <p style={{ color: '#64748b' }}>Carregando…</p>}

                    {dados && (
                        <>
                            {dados.EXCLUIDO_EM && (
                                <div style={{
                                    background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 12,
                                    padding: '13px 16px', marginBottom: 18, fontSize: 13.5, color: '#9a3412',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                                }}>
                                    <span><strong>Demanda excluída.</strong> {dados.EXCLUIDO_MOTIVO || 'sem motivo registrado'}</span>
                                    {/* Restaurar é ato de gestão. */}
                                    {podeGerir && (
                                        <button onClick={() => escrever(`${API}/${codigo}/restaurar`, 'POST')} disabled={salvando}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px',
                                                borderRadius: 8, border: '1px solid #fdba74', background: '#fff',
                                                color: '#9a3412', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', flexShrink: 0,
                                            }}><Undo2 size={14} /> Restaurar</button>
                                    )}
                                </div>
                            )}

                            {/* CABEÇALHO */}
                            <div style={{ ...caixa, borderLeft: `5px solid ${est.cor}` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                                            {/* O código em PRETO: ele é identificação, não estado.
                                                Colorido, competia com o selo ao lado. */}
                                            <span style={{ fontSize: 12, fontWeight: 800, color: TEXTO, letterSpacing: '.05em' }}>{dados.CODIGO}</span>
                                            <Selo cor={est.cor} fundo={est.fundo}>{est.rotulo}</Selo>
                                            {dados.PRIORIDADE && PRIORIDADE[dados.PRIORIDADE] && (
                                                <Selo cor={PRIORIDADE[dados.PRIORIDADE].cor} fundo={PRIORIDADE[dados.PRIORIDADE].fundo}>
                                                    prioridade {({ Alta: 'alta', Media: 'média', Baixa: 'baixa' })[dados.PRIORIDADE]}
                                                </Selo>
                                            )}
                                            {dados.TIPO && <Selo cor="#475569" fundo="#f1f5f9">{dados.TIPO}</Selo>}
                                        </div>
                                        {/* Demanda externa ainda não triada: TITULO é NULL,
                                            mostramos o que a pessoa escreveu (TITULO_ORIGINAL). */}
                                        <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 25, fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>
                                            {dados.TITULO || dados.TITULO_ORIGINAL}
                                        </h1>
                                    </div>
                                    {/* Lixeira do cabeçalho: só a GESTÃO. O solicitante exclui o
                                        próprio pedido pré-triagem pela faixa dedicada, abaixo. */}
                                    {podeGerir && !dados.EXCLUIDO_EM && (
                                        <button onClick={excluirDemanda} title="Excluir (erro de registro)"
                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#cbd5e1', padding: 4, flexShrink: 0 }}
                                            onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.color = '#cbd5e1'; }}>
                                            <Trash2 size={19} />
                                        </button>
                                    )}
                                </div>

                                {/* ATRIBUTOS EDITÁVEIS — só a GESTÃO (é a triagem). A diretoria não
                                    tria: vê prioridade/tipo pelos selos acima e edita o próprio
                                    pedido pela faixa dedicada.
                                    ⚠️ Eram só leitura até 17/09, e mudá-los exigia o CLI — o que
                                    deixava a tela incapaz de fazer justamente a TRIAGEM, que é a
                                    função declarada dela. Os `select` salvam no `onChange` (a
                                    escolha já é a confirmação) e os `input` no `onBlur`, para não
                                    disparar uma gravação por tecla digitada. */}
                                {podeGerir && (
                                <div style={{
                                    marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BORDA_SUAVE}`,
                                    display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12,
                                }}>
                                    <Campo rotulo="Prioridade">
                                        <select value={dados.PRIORIDADE || 'Media'} disabled={salvando}
                                            onChange={(e) => salvarCampo({ prioridade: e.target.value })}
                                            style={{ ...campoEstilo, fontWeight: dados.PRIORIDADE === 'Alta' ? 800 : 500, color: dados.PRIORIDADE === 'Alta' ? PRIORIDADE.Alta.cor : TEXTO }}>
                                            {['Alta', 'Media', 'Baixa'].map((p) => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </Campo>

                                    <Campo rotulo="Tipo">
                                        <select value={dados.TIPO || ''} disabled={salvando}
                                            onChange={(e) => salvarCampo({ tipo: e.target.value || null })}
                                            style={campoEstilo}>
                                            <option value="">— sem tipo —</option>
                                            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </Campo>

                                    <Campo rotulo="Responsável">
                                        <select value={dados.ID_RESPONSAVEL || ''} disabled={salvando}
                                            onChange={(e) => salvarCampo({ idResponsavel: e.target.value ? Number(e.target.value) : null })}
                                            style={campoEstilo}>
                                            <option value="">— sem responsável —</option>
                                            {equipe.map((u) => <option key={u.ID} value={u.ID}>{u.NOME_COMPLETO}</option>)}
                                        </select>
                                    </Campo>

                                    <Campo rotulo="Módulo">
                                        {/* Lista suspensa de dtbModulos + "+" para cadastrar um
                                            novo. O valor atual entra na lista mesmo se não estiver
                                            em dtbModulos (demanda antiga), para não sumir calado. */}
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <select value={dados.MODULO || ''} disabled={salvando}
                                                onChange={(e) => salvarCampo({ modulo: e.target.value || null })}
                                                style={{ ...campoEstilo, flex: 1 }}>
                                                <option value="">— sem módulo —</option>
                                                {[...new Set([...modulos, dados.MODULO].filter(Boolean))].map((m) => (
                                                    <option key={m} value={m}>{m}</option>
                                                ))}
                                            </select>
                                            <button type="button" onClick={criarModulo} disabled={salvando}
                                                title="Cadastrar novo módulo"
                                                style={{
                                                    flexShrink: 0, width: 40, borderRadius: 8, border: `1px solid ${BORDA}`,
                                                    background: '#fff', color: COR, fontWeight: 800, fontSize: 18,
                                                    cursor: salvando ? 'wait' : 'pointer', lineHeight: 1,
                                                }}>+</button>
                                        </div>
                                    </Campo>

                                    <Campo rotulo="Previsão">
                                        <input type="date" defaultValue={dados.DT_PREVISAO ? String(dados.DT_PREVISAO).slice(0, 10) : ''}
                                            disabled={salvando}
                                            onBlur={(e) => {
                                                const v = e.target.value || null;
                                                if (v !== (dados.DT_PREVISAO ? String(dados.DT_PREVISAO).slice(0, 10) : null)) salvarCampo({ dtPrevisao: v });
                                            }}
                                            style={campoEstilo} />
                                    </Campo>

                                </div>
                                )}

                                {/* Mesmos campos do bloco acima, mas como INFORMAÇÃO (não
                                    editável) para quem não gere — a diretoria precisa ver os
                                    dados básicos, só não mexe neles. */}
                                {!podeGerir && (
                                <div style={{
                                    marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BORDA_SUAVE}`,
                                    display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12,
                                }}>
                                    <Campo rotulo="Prioridade"><Valor>{dados.PRIORIDADE || '—'}</Valor></Campo>
                                    <Campo rotulo="Tipo"><Valor>{dados.TIPO || '—'}</Valor></Campo>
                                    {/* Responsável: leitura, EXCETO para o solicitante no
                                        próprio pedido ainda pré-triagem — aí ele pode APONTAR
                                        quem deve tocar (roteamento, 28/09). Depois de triado,
                                        volta a ser informação (a dupla é quem mexe). */}
                                    <Campo rotulo="Responsável">
                                        {podeMexerPedido ? (
                                            <select value={dados.ID_RESPONSAVEL || ''} disabled={salvando}
                                                onChange={(e) => salvarCampo({ idResponsavel: e.target.value ? Number(e.target.value) : null })}
                                                style={campoEstilo}>
                                                <option value="">— sem responsável —</option>
                                                {equipe.map((u) => <option key={u.ID} value={u.ID}>{u.NOME_COMPLETO}</option>)}
                                            </select>
                                        ) : (
                                            <Valor>{dados.RESPONSAVEL_NOME || 'sem responsável'}</Valor>
                                        )}
                                    </Campo>
                                    <Campo rotulo="Módulo"><Valor>{dados.MODULO || '—'}</Valor></Campo>
                                    <Campo rotulo="Previsão"><Valor>{dados.DT_PREVISAO ? String(dados.DT_PREVISAO).slice(0, 10) : '—'}</Valor></Campo>
                                    <Campo rotulo="Aberta em"><Valor>{dataHoraBR(dados.DT_CRIACAO)}</Valor></Campo>
                                    {dados.DT_CONCLUSAO && (
                                        <Campo rotulo={dados.STATUS === 'Recusada' ? 'Recusada em' : 'Concluída em'}>
                                            <Valor>{dataHoraBR(dados.DT_CONCLUSAO)}</Valor>
                                        </Campo>
                                    )}
                                </div>
                                )}

                                {(dados.DESCRICAO || dados.DESCRICAO_ORIGINAL) && (
                                    <p style={{ marginTop: 14, marginBottom: 0, fontSize: 14.5, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                        {dados.DESCRICAO || dados.DESCRICAO_ORIGINAL}
                                    </p>
                                )}

                                {/* Faixa do SOLICITANTE: corrigir ou excluir o próprio pedido,
                                    enquanto ele não foi triado. É a única escrita da diretoria aqui. */}
                                {podeMexerPedido && !editandoPedido && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 18, paddingTop: 16, borderTop: `1px solid ${BORDA_SUAVE}` }}>
                                        <button onClick={abrirEdicaoPedido} disabled={salvando}
                                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: `1px solid ${BORDA}`, background: '#fff', color: TEXTO, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                                            <Pencil size={14} /> Editar pedido
                                        </button>
                                        <button onClick={excluirMeuPedido} disabled={salvando}
                                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                                            <Trash2 size={14} /> Excluir pedido
                                        </button>
                                        <span style={{ fontSize: 12, color: '#94a3b8', alignSelf: 'center' }}>
                                            Enquanto o pedido está “Pendente de triagem”, você pode corrigi-lo ou excluí-lo.
                                        </span>
                                    </div>
                                )}

                                {/* Formulário de correção do pedido (mesmo conjunto do “Nova
                                    demanda”): título e descrição são o texto ORIGINAL. */}
                                {podeMexerPedido && editandoPedido && (
                                    <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${BORDA_SUAVE}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <input autoFocus value={pedido.titulo} maxLength={150}
                                            onChange={(e) => setPedido({ ...pedido, titulo: e.target.value })}
                                            placeholder="Título do pedido (até 150 caracteres)"
                                            style={campoEstilo} />
                                        <textarea value={pedido.descricao} maxLength={4000} rows={4}
                                            onChange={(e) => setPedido({ ...pedido, descricao: e.target.value })}
                                            placeholder="Descrição (opcional) — o que precisa, por quê, o contexto"
                                            style={{ ...campoEstilo, resize: 'vertical' }} />
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            <select value={pedido.prioridade} onChange={(e) => setPedido({ ...pedido, prioridade: e.target.value })}
                                                style={{ ...campoEstilo, width: 'auto' }}>
                                                {['Alta', 'Media', 'Baixa'].map((p) => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                            <select value={pedido.modulo} onChange={(e) => setPedido({ ...pedido, modulo: e.target.value })}
                                                style={{ ...campoEstilo, width: 'auto' }}>
                                                <option value="">módulo…</option>
                                                {[...new Set([...modulos, pedido.modulo].filter(Boolean))].map((m) => <option key={m} value={m}>{m}</option>)}
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button onClick={salvarPedido} disabled={salvando || !pedido.titulo.trim()}
                                                style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: COR, color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: pedido.titulo.trim() ? 'pointer' : 'not-allowed', opacity: pedido.titulo.trim() ? 1 : .5 }}>
                                                Salvar
                                            </button>
                                            <button onClick={() => setEditandoPedido(false)} disabled={salvando}
                                                style={{ padding: '9px 16px', borderRadius: 8, border: `1px solid ${BORDA}`, background: '#fff', color: '#475569', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Mudar de estado é a ação mais usada — fica à mão, no cabeçalho.
                                    Só a GESTÃO muda status (a diretoria não tria). */}
                                {podeGerir && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 18, paddingTop: 16, borderTop: `1px solid ${BORDA_SUAVE}` }}>
                                    {Object.keys(STATUS).map((s) => {
                                        const e = doStatus(s);
                                        const atual = dados.STATUS === s;
                                        return (
                                            // ⚠️ Só o estado ATUAL se pinta por dentro. Os outros ficam
                                            //    com texto preto e a borda na cor — a borda basta para
                                            //    dizer "este é o vermelho", e seis textos coloridos lado
                                            //    a lado faziam a barra inteira parecer alerta.
                                            <button key={s} disabled={salvando} onClick={() => mudarStatus(s)}
                                                style={{
                                                    padding: '7px 13px', borderRadius: 999, fontSize: 12.5, fontWeight: 700,
                                                    cursor: salvando ? 'wait' : 'pointer',
                                                    border: `1.5px solid ${e.cor}`,
                                                    background: atual ? e.cor : '#fff',
                                                    color: atual ? '#fff' : TEXTO,
                                                }}>{e.rotulo}</button>
                                        );
                                    })}
                                </div>
                                )}
                            </div>

                            {/* PEDIDO ORIGINAL — só depois de triado (TITULO preenchido pelo
                                Claude). Antes disso, o título/descrição acima JÁ é o original,
                                então mostrar o box duplicaria o texto. */}
                            {dados.TITULO_ORIGINAL && dados.TITULO && (
                                <div style={{ ...caixa, background: '#f8fafc' }}>
                                    <p style={{ margin: '0 0 8px', fontWeight: 800, fontSize: 11.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                                        Pedido original — {dados.SOLICITANTE_NOME || 'solicitante'}
                                    </p>
                                    <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#334155' }}>{dados.TITULO_ORIGINAL}</p>
                                    {dados.DESCRICAO_ORIGINAL && (
                                        <p style={{ margin: '8px 0 0', fontSize: 13.5, color: '#475569', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                            {dados.DESCRICAO_ORIGINAL}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* TAREFAS */}
                            <div style={caixa}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                                    <h2 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                                        Tarefas — {dados.TAREFAS_FEITAS}/{dados.TAREFAS_TOTAL}
                                    </h2>
                                    {podeGerir && (
                                        <button onClick={() => setVerExcluidas((v) => !v)}
                                            style={{ border: 'none', background: 'transparent', color: '#94a3b8', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                                            {verExcluidas ? 'ocultar excluídas' : 'ver excluídas'}
                                        </button>
                                    )}
                                </div>

                                {dados.TAREFAS_TOTAL > 0 && (
                                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden', marginBottom: 16 }}>
                                        <div style={{ height: '100%', width: `${Math.round((dados.TAREFAS_FEITAS / dados.TAREFAS_TOTAL) * 100)}%`, background: COR }} />
                                    </div>
                                )}

                                {agruparPorSecao(dados.tarefas).map(([secao, itens]) => (
                                    <div key={secao || '_'} style={{ marginBottom: 16 }}>
                                        {secao && (
                                            <p style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 800, color: COR_ESCURA }}>{secao}</p>
                                        )}
                                        {itens.map((t) => {
                                            const feita = t.STATUS === 'Concluida';
                                            const cancelada = t.STATUS === 'Cancelada';
                                            const excluida = !!t.EXCLUIDO_EM;
                                            const inerte = cancelada || excluida;
                                            return (
                                                <div key={t.ID}
                                                    style={{
                                                        display: 'flex', alignItems: 'flex-start', gap: 9,
                                                        padding: '8px 10px', borderRadius: 8,
                                                        background: excluida ? '#fff7ed' : feita ? COR_FUNDO : 'transparent',
                                                        opacity: excluida ? 0.75 : 1,
                                                    }}>
                                                    <span onClick={() => podeGerir && !inerte && alternarTarefa(t)}
                                                        style={{ marginTop: 1, color: feita ? COR : '#cbd5e1', flexShrink: 0, cursor: (inerte || !podeGerir) ? 'default' : 'pointer' }}>
                                                        {excluida ? <Trash2 size={16} /> : cancelada ? <Ban size={16} /> : feita ? <Check size={16} /> : <Circle size={16} />}
                                                    </span>
                                                    {/* "posição · #ID" (tarefa #10): a posição (1..N, só das
                                                        vivas) ordena para o olho; o #ID é a REFERÊNCIA estável
                                                        — é o que se cita no andamento e o que o CLI endereça,
                                                        e não escorrega quando a lista é remexida. A excluída
                                                        não tem posição: mostra só o #ID. */}
                                                    <span style={{ flexShrink: 0, textAlign: 'right', fontSize: 12.5, marginTop: 1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                                        {t.NUM != null && <span style={{ fontWeight: 700, color: '#94a3b8' }}>{t.NUM} · </span>}
                                                        <span style={{ fontWeight: 700, color: COR_ESCURA }}>#{t.ID}</span>
                                                    </span>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        {tarefaEditando === t.ID ? (
                                                            // Edição inline: Enter salva, Esc cancela (também há os
                                                            // botões de check/X à direita). autoFocus para já digitar.
                                                            <input autoFocus
                                                                value={textoEditando}
                                                                onChange={(e) => setTextoEditando(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') salvarEdicao(t);
                                                                    if (e.key === 'Escape') cancelarEdicao();
                                                                }}
                                                                style={{
                                                                    width: '100%', boxSizing: 'border-box', fontSize: 13.5, lineHeight: 1.55,
                                                                    padding: '4px 8px', border: `1px solid ${COR}`, borderRadius: 6, fontFamily: 'inherit',
                                                                }} />
                                                        ) : (
                                                            // ⚠️ O texto NÃO alterna a conclusão. Só a bolinha à esquerda
                                                            //    marca/desmarca — clicar no texto marcava sem querer ao
                                                            //    tentar selecionar/copiar. cursor 'text' convida a seleção.
                                                            <span
                                                                style={{
                                                                    display: 'block', fontSize: 13.5, lineHeight: 1.55,
                                                                    cursor: 'text',
                                                                    color: inerte ? '#94a3b8' : feita ? COR_ESCURA : '#334155',
                                                                    textDecoration: inerte ? 'line-through' : 'none',
                                                                }}>
                                                                {t.DESCRICAO}
                                                                {excluida && (
                                                                    <em style={{ display: 'block', fontSize: 11.5, color: '#9a3412', fontStyle: 'normal', marginTop: 2 }}>
                                                                        excluída: {t.EXCLUIDO_MOTIVO || 'sem motivo'}
                                                                    </em>
                                                                )}
                                                            </span>
                                                        )}
                                                        {/* Datas discretas, EMBAIXO do texto: criação para TODAS as
                                                            tarefas, conclusão só para as concluídas. Cinza e pequenas
                                                            de propósito — informam sem competir com a tarefa. A
                                                            excluída não mostra (a linha dela já diz o motivo). */}
                                                        {!excluida && (
                                                            <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8', lineHeight: 1.5, fontVariantNumeric: 'tabular-nums' }}>
                                                                Data Criação: {dataHoraBR(t.DT_CRIACAO)}
                                                                {t.DT_CONCLUSAO && (
                                                                    <>
                                                                        {/* divisor sutil, mais claro que o texto */}
                                                                        <span style={{ margin: '0 7px', color: '#cbd5e1' }}>|</span>
                                                                        Data Conclusão: {dataHoraBR(t.DT_CONCLUSAO)}
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Ações da tarefa: só a GESTÃO. Para a diretoria, a
                                                        tarefa é leitura. */}
                                                    {podeGerir && (excluida ? (
                                                        <button onClick={() => escrever(`${API}/tarefa/${t.ID}/restaurar`, 'POST')} title="Restaurar"
                                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9a3412', padding: '0 2px', flexShrink: 0 }}>
                                                            <Undo2 size={14} />
                                                        </button>
                                                    ) : tarefaEditando === t.ID ? (
                                                        <>
                                                            <button onClick={() => salvarEdicao(t)} title="Salvar" disabled={salvando}
                                                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: COR, padding: '0 2px', flexShrink: 0 }}>
                                                                <Check size={15} />
                                                            </button>
                                                            <button onClick={cancelarEdicao} title="Cancelar"
                                                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', padding: '0 2px', flexShrink: 0 }}>
                                                                <X size={15} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {/* Reativar: só numa tarefa CANCELADA (e com a demanda aberta).
                                                                Desfaz a decisão — volta para "A fazer". É o par do restaurar
                                                                da exclusão. */}
                                                            {demandaAberta && cancelada && (
                                                                <button onClick={() => reativarTarefa(t)} title="Reativar (voltar para A fazer)" disabled={salvando}
                                                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', padding: '0 2px', flexShrink: 0 }}
                                                                    onMouseEnter={(e) => { e.currentTarget.style.color = COR; }}
                                                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}>
                                                                    <RotateCcw size={13} />
                                                                </button>
                                                            )}
                                                            {/* Editar o TEXTO da tarefa: liberado só com a demanda aberta
                                                                (numa encerrada o checklist é histórico), nunca numa tarefa
                                                                inerte e nunca numa JÁ CONCLUÍDA — a feita está "fechada"
                                                                (para reescrever, desmarca na bolinha, edita e remarca). O
                                                                antigo botão "Fazendo" da tarefa foi retirado — quem fica
                                                                "em andamento" é a DEMANDA; a tarefa é feita ou não. */}
                                                            {demandaAberta && !inerte && !feita && (
                                                                <button onClick={() => iniciarEdicao(t)} title="Editar o texto"
                                                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#cbd5e1', padding: '0 2px', flexShrink: 0 }}
                                                                    onMouseEnter={(e) => { e.currentTarget.style.color = COR; }}
                                                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#cbd5e1'; }}>
                                                                    <Pencil size={13} />
                                                                </button>
                                                            )}
                                                            {/* Cancelar (decisão, fica visível): só em tarefa ativa (não
                                                                inerte, não concluída) e com a demanda aberta. Âmbar no hover
                                                                para separar de excluir (vermelho) — decisão, não erro. */}
                                                            {demandaAberta && !inerte && !feita && (
                                                                <button onClick={() => cancelarTarefa(t)} title="Cancelar (decisão: fazia sentido, deixou de fazer)" disabled={salvando}
                                                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#cbd5e1', padding: '0 2px', flexShrink: 0 }}
                                                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#b45309'; }}
                                                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#cbd5e1'; }}>
                                                                    <Ban size={13} />
                                                                </button>
                                                            )}
                                                            <button onClick={() => excluirTarefa(t)} title="Excluir (erro de registro)"
                                                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#cbd5e1', padding: '0 2px', flexShrink: 0 }}
                                                                onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.color = '#cbd5e1'; }}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}

                                {/* NOVA TAREFA — a tela deixou de ser só leitura (pedido do Rodrigo, 17/09).
                                    Quebrar a demanda em tarefas é trabalho da GESTÃO. */}
                                {podeGerir && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, paddingTop: 16, borderTop: `1px solid ${BORDA_SUAVE}` }}>
                                    <input
                                        value={novaTarefa.secao}
                                        onChange={(e) => setNovaTarefa({ ...novaTarefa, secao: e.target.value })}
                                        list="secoes-existentes"
                                        maxLength={120}
                                        placeholder="Seção (opcional)"
                                        style={{ flex: '0 1 190px', padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDA}`, fontSize: 13.5, fontFamily: 'inherit' }}
                                    />
                                    {/* As seções que já existem viram sugestão — é o que evita
                                        "3.1 Banco" e "3.1 banco" virarem dois grupos. */}
                                    <datalist id="secoes-existentes">
                                        {[...new Set(dados.tarefas.map((t) => t.SECAO).filter(Boolean))].map((s) => <option key={s} value={s} />)}
                                    </datalist>
                                    <input
                                        value={novaTarefa.desc}
                                        onChange={(e) => setNovaTarefa({ ...novaTarefa, desc: e.target.value })}
                                        onKeyDown={(e) => e.key === 'Enter' && criarTarefa()}
                                        maxLength={500}
                                        placeholder="Nova tarefa — descreva o que precisa ser feito"
                                        style={{ flex: '1 1 260px', padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDA}`, fontSize: 13.5, fontFamily: 'inherit' }}
                                    />
                                    <button onClick={criarTarefa} disabled={salvando || !novaTarefa.desc.trim()}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                                            borderRadius: 8, border: 'none', background: COR, color: '#fff',
                                            fontWeight: 700, fontSize: 13.5,
                                            cursor: novaTarefa.desc.trim() ? 'pointer' : 'not-allowed',
                                            opacity: novaTarefa.desc.trim() ? 1 : .5,
                                        }}><Plus size={15} /> Adicionar</button>
                                </div>
                                )}
                            </div>

                            {/* ANDAMENTOS */}
                            <div style={caixa}>
                                <h2 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '.05em', display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <MessageSquare size={14} /> Andamentos
                                </h2>

                                {/* Comentar andamento é da GESTÃO por ora (a diretoria vê, não comenta). */}
                                {podeGerir && (
                                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                                    <input
                                        value={novoAndamento}
                                        onChange={(e) => setNovoAndamento(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && enviarAndamento()}
                                        maxLength={2000}
                                        placeholder="O que aconteceu? Ex.: parado esperando a Sovos responder"
                                        style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDA}`, fontSize: 13.5, fontFamily: 'inherit' }}
                                    />
                                    <button onClick={enviarAndamento} disabled={salvando || !novoAndamento.trim()}
                                        style={{
                                            padding: '0 15px', borderRadius: 8, border: 'none', background: COR,
                                            color: '#fff', cursor: 'pointer', opacity: novoAndamento.trim() ? 1 : .5,
                                        }}><Send size={16} /></button>
                                </div>
                                )}

                                {dados.andamentos.length === 0 && (
                                    <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Nenhum andamento ainda.</p>
                                )}
                                <div style={listaRolavel}>
                                    {dados.andamentos.map((a) => (
                                        <div key={a.ID} style={{ padding: '9px 0', borderBottom: `1px solid ${BORDA_SUAVE}` }}>
                                            <p style={{ margin: 0, fontSize: 11.5, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <Clock size={11} /> {quando(a.DT)} · {a.AUTOR_NOME || 'sessão de trabalho'}
                                            </p>
                                            <p style={{ margin: '3px 0 0', fontSize: 13.5, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                                {a.TEXTO}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* COMMITS */}
                            {dados.commits.length > 0 && (
                                <div style={caixa}>
                                    <h2 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '.05em', display: 'flex', alignItems: 'center', gap: 7 }}>
                                        <GitCommit size={14} /> Commits ({dados.commits.length})
                                    </h2>
                                    <div style={listaRolavel}>
                                        {dados.commits.map((c) => (
                                            <div key={c.HASH} style={{ padding: '7px 0', fontSize: 13, color: '#475569', borderBottom: `1px solid ${BORDA_SUAVE}` }}>
                                                <code style={{ color: COR_ESCURA, fontWeight: 700 }}>{c.HASH.slice(0, 8)}</code>
                                                {' '}<span style={{ color: '#94a3b8' }}>{dataHora(c.DT)}</span> — {c.MENSAGEM}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Valor em texto (leitura), no lugar do `select`/`input` — para a diretoria, que
 *  vê os atributos como informação, não como campo editável. Mesma altura visual
 *  dos campos editáveis, para as duas telas ficarem parecidas. */
function Valor({ children }) {
    return (
        <span style={{
            padding: '8px 10px', borderRadius: 8, border: `1px solid ${BORDA_SUAVE}`,
            fontSize: 13.5, color: TEXTO, background: '#f8fafc',
        }}>{children}</span>
    );
}

/** Rótulo pequeno + campo, o par que se repete no bloco de atributos. */
function Campo({ rotulo, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {rotulo}
            </span>
            {children}
        </label>
    );
}

function Selo({ cor, fundo, children }) {
    return (
        <span style={{
            fontSize: 11, fontWeight: 800, color: cor, background: fundo,
            border: `1px solid ${cor}33`, padding: '3px 9px', borderRadius: 999,
            textTransform: 'uppercase', letterSpacing: '.03em',
        }}>{children}</span>
    );
}

/** Agrupa mantendo a ordem em que o backend devolveu (ele já ordena por seção/ordem). */
function agruparPorSecao(tarefas) {
    const mapa = new Map();
    for (const t of tarefas) {
        const chave = t.SECAO || '';
        if (!mapa.has(chave)) mapa.set(chave, []);
        mapa.get(chave).push(t);
    }
    return [...mapa.entries()];
}

export default DemandaDetalhe;
